import { useState, type FormEvent, type ReactNode } from 'react';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { getToken, login } from '@/services/authService';

/** Si no hay token, muestra el login; si lo hay, renderiza el dashboard. */
export function LoginGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(() => !!getToken());
  if (authed) return <>{children}</>;
  return <LoginForm onSuccess={() => setAuthed(true)} />;
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Login SIN atar a una empresa: el BFF autentica y trae TODAS las empresas del usuario
  // (el filtro de empresa vive dentro del dashboard). Mandar 'vg' fijo bloqueaba a los
  // usuarios que no tienen Vine Garden (ej. SmartBamboo-only) con "Credenciales inválidas".
  const company = '';
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password, company);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al entrar');
    } finally {
      setLoading(false);
    }
  }

  const fieldWrap =
    'flex items-center gap-2 rounded-xl border border-slate-300 px-3 transition focus-within:border-red-600 focus-within:ring-1 focus-within:ring-red-600';

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-to-br from-[#7a1717] via-[#4a0d0d] to-[#220505] p-4">
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit} className="rounded-3xl bg-white p-8 shadow-2xl">
          <div className="mb-7 text-center">
            <img src="/logo-sg.png" alt="Salo & Gaash" className="mx-auto h-24 w-auto invert" />
            <p className="mt-2 text-xl font-bold text-slate-800">Dashboard</p>
            <p className="mt-1 text-sm text-slate-400">Ingresa con tus credenciales de Odoo</p>
          </div>

          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Usuario</label>
          <div className={`mb-4 ${fieldWrap}`}>
            <User className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              className="w-full bg-transparent py-2.5 text-sm outline-none"
              type="email"
              placeholder="correo@vinegarden.mx"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Contraseña</label>
          <div className={`mb-2 ${fieldWrap}`}>
            <Lock className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              className="w-full bg-transparent py-2.5 text-sm outline-none"
              type={showPwd ? 'text' : 'password'}
              placeholder="Ingresa tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="button" onClick={() => setShowPwd((s) => !s)} className="shrink-0 text-slate-400 hover:text-slate-600">
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <div className="mb-5" />

          {error && <p className="mb-3 text-center text-sm font-medium text-red-600">{error}</p>}

          <button
            className="w-full rounded-xl bg-red-700 py-3 text-sm font-bold text-white shadow-lg shadow-red-700/30 transition hover:bg-red-800 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Entrando…' : 'Iniciar Sesión'}
          </button>
        </form>
        <p className="mt-5 text-center text-xs font-medium text-white/60">Dashboard - Salo&amp;Gaash</p>
      </div>
    </div>
  );
}
